import { useState } from 'react';
import { Check, Magnet } from 'lucide-react';
import { useStudioStore } from '../state/useStudioStore';
import { formatLength, parseLength } from '../utils/units';

export function StatusBar(): JSX.Element {
  const document = useStudioStore((state) => state.document);
  const activeTool = useStudioStore((state) => state.activeTool);
  const status = useStudioStore((state) => state.statusMessage);
  const cursor = useStudioStore((state) => state.cursorWorld);
  const selectedIds = useStudioStore((state) => state.selectedIds);
  const measurementResult = useStudioStore((state) => state.measurementResult);
  const updateEntity = useStudioStore((state) => state.updateEntity);
  const updateSettings = useStudioStore((state) => state.updateSettings);
  const setStatus = useStudioStore((state) => state.setStatus);
  const [value, setValue] = useState('');

  const selected = selectedIds.length === 1
    ? document.entities.find((entity) => entity.id === selectedIds[0])
    : undefined;

  const applyValue = () => {
    const metres = parseLength(value, document.settings.units);
    if (metres === null || metres <= 0) {
      setStatus('Enter a valid positive length, for example 2400mm or 8\' 2".');
      return;
    }
    if (!selected) {
      setStatus('Select one element before entering an exact dimension.');
      return;
    }

    const dimensions = { ...selected.dimensions };
    const position = [...selected.position] as [number, number, number];
    if (activeTool === 'pushpull' || selected.kind === 'box' || selected.kind === 'column' || selected.kind === 'slab') {
      const base = selected.position[1] - selected.dimensions.height / 2;
      dimensions.height = metres;
      position[1] = base + metres / 2;
    } else {
      dimensions.width = metres;
    }
    updateEntity(selected.id, { dimensions, position });
    setStatus(`Applied ${formatLength(metres, document.settings.units)} to ${selected.name}`);
    setValue('');
  };

  const cursorLabel = cursor
    ? `X ${cursor[0].toFixed(2)}  Y ${cursor[1].toFixed(2)}  Z ${cursor[2].toFixed(2)}`
    : 'X —  Y —  Z —';

  return (
    <footer className="statusbar">
      <div className="status-tool"><strong>{activeTool === 'pushpull' ? 'Push / Pull' : activeTool}</strong><span>{status}</span></div>
      <div className="status-coordinates">{cursorLabel}</div>
      {measurementResult !== null ? (
        <div className="status-measure">Measured <strong>{formatLength(measurementResult, document.settings.units)}</strong></div>
      ) : null}
      <button
        type="button"
        className={`snap-toggle ${document.settings.snapEnabled ? 'is-on' : ''}`}
        title="Toggle grid snapping"
        onClick={() => updateSettings({ snapEnabled: !document.settings.snapEnabled })}
      >
        <Magnet size={15} /> Snap
      </button>
      <label className="measurement-box">
        <span>Exact value</span>
        <input
          value={value}
          placeholder={selected ? (activeTool === 'pushpull' ? 'Height' : 'Length / height') : 'Select an element'}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') applyValue();
          }}
        />
        <button type="button" title="Apply exact value" onClick={applyValue}><Check size={15} /></button>
      </label>
    </footer>
  );
}
